import type { ReactNode } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';

interface Props {
  label: string;
  /** Marked on the label, never by placeholder text (§4 Forms). */
  required?: boolean;
  /** Rendered below the field in `text-xs text-negative` — never an alert, never a toast. */
  error?: string;
  /**
   * A **description** of the field — why it is inert, what shape a value takes, what the reader should
   * do — rendered below it in ink and tied to the control with `aria-describedby` (BUG-022).
   *
   * ⚠️ **It is not an error and must not be rendered as one.** `error` sets `aria-invalid`, which tells
   * assistive technology the value is wrong; a description says nothing about validity. §6.4's
   * empty-catalogue sentence is the case this exists for: the field is `disabled` because the tenant has
   * no vacancies, which is information rather than a fault — and a `disabled` control is **out of the tab
   * order**, so a reader moving between the form's controls never reached it and never heard why the last
   * required field was inert. Visually the sentence sat right beside it; non-visually it was connected to
   * nothing.
   *
   * Both may be present: `aria-describedby` then names the description **and** the error, in that order,
   * because the description is the standing fact and the error is what just happened.
   */
  description?: ReactNode;
  children: (fieldProps: {
    id: string;
    'aria-invalid'?: true;
    'aria-describedby'?: string;
  }) => ReactNode;
  className?: string;
}

/**
 * The form field wrapper of `DESIGN.md` §4: label above, control, description and error below. A field
 * error is a property of the field, so it renders next to it — routing one through a toast is how a user
 * ends up hunting for which input went wrong.
 *
 * ⚠️ **It wires `aria-describedby` for **both** slots since BUG-022.** It knew only about `error` before,
 * so every explanatory sentence in the product was a sibling `<p>` connected to nothing — and the one
 * place it mattered most was a **disabled** control, which no keyboard user can reach to hear its own
 * label, let alone a sentence sitting beside it.
 */
export function Field({ label, required = false, error, description, children, className }: Props) {
  const id = useId();
  const errorId = `${id}-error`;
  const descriptionId = `${id}-description`;
  const { t } = useTranslation('common');

  /*
   * The description first, the error second: one is a standing fact about the field and the other is
   * what just happened, and a screen reader announces them in this order.
   */
  const describedBy =
    [description ? descriptionId : '', error ? errorId : ''].join(' ').trim() || undefined;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label htmlFor={id} className="text-xs font-medium text-ink-700">
        {label}
        {required && (
          <span className="ml-1 text-ink-500" aria-label={t('required')}>
            *
          </span>
        )}
      </label>
      {children({
        id,
        ...(error ? { 'aria-invalid': true as const } : {}),
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      })}
      {description && (
        <div id={descriptionId} className="text-xs text-ink-700">
          {description}
        </div>
      )}
      {error && (
        <p id={errorId} className="text-xs text-negative">
          {error}
        </p>
      )}
    </div>
  );
}
